import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { PRODUCT_TOUR_STEPS, type ProductTourView } from "./product-tour-steps.js";
import { dismissProductTourForSession, markProductTourCompleted } from "./product-tour-storage.js";
import {
  TOUR_PAD,
  TOUR_SPOTLIGHT_RADIUS,
  TOUR_TIP_WIDTH,
  clampTourSpotlightToTargetSurface,
  readTourTargetRect,
  tourTipPosition,
  type TourTargetRect,
} from "./product-tour-layout.js";
import { ProductTourTip } from "./ProductTourTip.js";

export function ProductTour({
  employeeId,
  roleTitle,
  open,
  view,
  onRequestView,
  onRequestPath,
  onClose,
}: {
  employeeId: string;
  roleTitle: string;
  open: boolean;
  view: ProductTourView;
  onRequestView: (view: ProductTourView) => void;
  onRequestPath: (pathWithQuery: string) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [index, setIndex] = useState(0);
  const [target, setTarget] = useState<TourTargetRect | null>(null);
  const [tipHeight, setTipHeight] = useState(280);
  const tipRef = useRef<HTMLElement>(null);
  const step = PRODUCT_TOUR_STEPS[index] ?? PRODUCT_TOUR_STEPS[0];
  const total = PRODUCT_TOUR_STEPS.length;
  const isLast = index === total - 1;
  const isFirst = index === 0;
  const centered = !step?.targetControlId;
  const tipWidth = TOUR_TIP_WIDTH;
  const primaryControlId = isLast
    ? "product-tour.done"
    : isFirst
      ? "product-tour.start"
      : "product-tour.next";

  const finish = useCallback(() => {
    markProductTourCompleted(employeeId);
    dismissProductTourForSession(employeeId);
    onClose();
  }, [employeeId, onClose]);

  useEffect(() => {
    if (!open) return;
    setIndex(0);
  }, [open, employeeId]);

  useEffect(() => {
    if (!open || !step) return;
    onRequestPath(step.preferredPath);
    if (step.preferredView && step.preferredView !== view) onRequestView(step.preferredView);
  }, [open, onRequestPath, onRequestView, step, view]);

  useLayoutEffect(() => {
    if (!open || !step) return;
    let frame = 0;
    const measure = () => {
      if (centered) {
        setTarget(null);
        return;
      }
      if (step.targetControlId) {
        const candidates = [step.targetControlId, `${step.targetControlId}.mobile`];
        for (const candidate of candidates) {
          const node = document.querySelector(`[data-control-id="${candidate}"]`);
          if (node instanceof HTMLElement) {
            node.scrollIntoView({ block: "nearest", inline: "nearest" });
            break;
          }
        }
      }
      setTarget(readTourTargetRect(step.targetControlId));
      if (tipRef.current) setTipHeight(tipRef.current.getBoundingClientRect().height);
    };
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };
    const delay = window.setTimeout(schedule, centered ? 0 : 120);
    window.addEventListener("resize", schedule);
    window.addEventListener("scroll", schedule, true);
    return () => {
      window.clearTimeout(delay);
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", schedule);
      window.removeEventListener("scroll", schedule, true);
    };
  }, [open, step, view, index, centered]);

  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const primary = document.querySelector(`[data-control-id="${primaryControlId}"]`);
    if (primary instanceof HTMLElement) primary.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") finish();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus();
    };
  }, [open, index, primaryControlId, finish]);

  const position = useMemo(() => tourTipPosition(target, tipHeight, tipWidth), [target, tipHeight, tipWidth]);

  if (!open || !step) return null;

  const spotlight =
    !centered && target
      ? clampTourSpotlightToTargetSurface(
          {
            top: target.top - TOUR_PAD,
            left: target.left - TOUR_PAD,
            width: target.width + TOUR_PAD * 2,
            height: target.height + TOUR_PAD * 2,
          },
          step.targetControlId,
        )
      : null;

  return createPortal(
    <div
      className={`fixed inset-0 z-[100] ${centered ? "grid place-items-center p-1" : ""}`}
      data-control-id="product-tour"
      role="presentation"
    >
      {spotlight ? (
        <svg aria-hidden="true" className="pointer-events-none absolute inset-0 h-full w-full">
          <defs>
            <mask id="product-tour-spotlight-mask" maskUnits="userSpaceOnUse">
              <rect fill="white" height="100%" width="100%" x="0" y="0" />
              <rect
                fill="black"
                height={spotlight.height}
                rx={TOUR_SPOTLIGHT_RADIUS}
                ry={TOUR_SPOTLIGHT_RADIUS}
                width={spotlight.width}
                x={spotlight.left}
                y={spotlight.top}
              />
            </mask>
          </defs>
          <rect
            fill="color-mix(in srgb, var(--foreground) 45%, transparent)"
            height="100%"
            mask="url(#product-tour-spotlight-mask)"
            width="100%"
            x="0"
            y="0"
          />
        </svg>
      ) : (
        <div aria-hidden="true" className="absolute inset-0 bg-foreground/45" />
      )}
      {spotlight ? (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute rounded-2xl ring-2 ring-accent"
          style={{
            top: spotlight.top,
            left: spotlight.left,
            width: spotlight.width,
            height: spotlight.height,
          }}
        />
      ) : null}
      <ProductTourTip
        body={t(`productTour.steps.${step.id}.body`)}
        centered={centered}
        onClose={finish}
        onPrimary={() => {
          if (isLast) {
            finish();
            return;
          }
          setIndex((value) => Math.min(total - 1, value + 1));
        }}
        onSecondary={() => {
          if (isFirst) {
            finish();
            return;
          }
          setIndex((value) => Math.max(0, value - 1));
        }}
        position={position}
        primaryControlId={primaryControlId}
        primaryLabel={
          isLast ? t("productTour.done") : isFirst ? t("productTour.start") : t("productTour.next")
        }
        progressLabel={t("productTour.progress", { current: index + 1, total })}
        secondaryControlId={isFirst ? "product-tour.later" : "product-tour.back"}
        secondaryLabel={isFirst ? t("productTour.later") : t("productTour.back")}
        stepId={step.id}
        tipRef={tipRef}
        title={t(`productTour.steps.${step.id}.title`, { role: roleTitle })}
      />
    </div>,
    document.body,
  );
}
